/**
 * CORE-1598 — neural noise suppression for the room microphone (web).
 *
 * Prod, 05.10.2026: listeners heard the speaker's keyboard loudly. The browser's
 * own suppression was ON for every Chrome/Edge mic (`audio_capture_params`
 * `noiseSuppression: true`, 200 of 200 rows) — WebRTC's suppressor removes
 * stationary noise (hum, fans) and lets short transients like key clicks through.
 * RNNoise (xiph, a small recurrent network) is trained on exactly that kind of
 * noise and runs in an AudioWorklet next to the capture.
 *
 * Assets: the worklet and wasm are copies from `@sapphi-red/web-noise-suppressor`
 * under `public/vendor/rnnoise` (kept equal to the package by a jest guard). The
 * package's main module is NOT imported: it declares `class … extends
 * AudioWorkletNode` at module level and would throw on native and in jest. The
 * node is created here by its registered processor name instead.
 *
 * Every failure falls back to the unprocessed mic — a missing denoiser must
 * never cost a person their voice.
 */
import { Platform } from 'react-native';
import { micFitsWebAudioGraph } from './micWebAudioGuard';

export const RNNOISE_PROCESSOR_ID = '@sapphi-red/web-noise-suppressor/rnnoise';
export const RNNOISE_ASSET_BASE = '/vendor/rnnoise';
/** RNNoise works on 480-sample frames of 48 kHz audio; any other rate garbles speech. */
export const RNNOISE_SAMPLE_RATE = 48_000;

/**
 * What the mic actually went out with — reported in `audio_capture_params`.
 * `headset` — the flag is on, but the mic is a headset or captures at a rate other
 * than 48 kHz and stays off the WebAudio graph (CORE-1669); it goes out unprocessed.
 */
export type DenoiserState = 'off' | 'pending' | 'rnnoise' | 'unsupported' | 'failed' | 'headset';

const attached = new WeakMap<object, () => DenoiserState | null>();

/** The denoiser state of a mic track (Agora or LiveKit); 'off' if none was attached. */
export function denoiserStateOf(track: unknown): DenoiserState {
    const read = track && typeof track === 'object' ? attached.get(track) : undefined;
    return read ? (read() ?? 'pending') : 'off';
}

/** Whether the mic's sound passes through the WebAudio graph because of the denoiser. */
export function denoiserOnGraph(state: DenoiserState): boolean {
    return state !== 'off' && state !== 'headset';
}

// Smallest module using a SIMD opcode (wasm-feature-detect, as the package does).
const SIMD_PROBE = new Uint8Array([
    0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11,
]);

let wasmPromise: Promise<ArrayBuffer> | null = null;
const workletLoads = new WeakMap<BaseAudioContext, Promise<void>>();

function loadWasm(): Promise<ArrayBuffer> {
    if (!wasmPromise) {
        wasmPromise = (async () => {
            let simd = false;
            try { simd = WebAssembly.validate(SIMD_PROBE); } catch { /* plain build */ }
            const res = await fetch(`${RNNOISE_ASSET_BASE}/${simd ? 'rnnoise_simd.wasm' : 'rnnoise.wasm'}`);
            if (!res.ok) throw new Error(`rnnoise wasm: HTTP ${res.status}`);
            return res.arrayBuffer();
        })();
        // A failed fetch is retried by the next mic, not cached forever.
        wasmPromise.catch(() => { wasmPromise = null; });
    }
    return wasmPromise;
}

/** Whether RNNoise can run in this context at all. */
export function canDenoise(ctx: BaseAudioContext | null | undefined): ctx is AudioContext {
    return Platform.OS === 'web'
        && !!ctx
        && ctx.sampleRate === RNNOISE_SAMPLE_RATE
        && typeof AudioWorkletNode !== 'undefined'
        && !!(ctx as AudioContext).audioWorklet;
}

export async function createRnnoiseNode(ctx: AudioContext): Promise<AudioWorkletNode> {
    let load = workletLoads.get(ctx);
    if (!load) {
        load = ctx.audioWorklet.addModule(`${RNNOISE_ASSET_BASE}/rnnoiseWorklet.js`);
        workletLoads.set(ctx, load);
        load.catch(() => { workletLoads.delete(ctx); });
    }
    const [wasmBinary] = await Promise.all([loadWasm(), load]);
    return new AudioWorkletNode(ctx, RNNOISE_PROCESSOR_ID, { processorOptions: { maxChannels: 2, wasmBinary } });
}

export function destroyRnnoiseNode(node: AudioWorkletNode | null | undefined): void {
    if (!node) return;
    try { node.disconnect(); } catch { /* already detached */ }
    try { node.port.postMessage('destroy'); } catch { /* worklet gone */ }
}

// ─── Agora: a processor in the track's own pipeline ─────────────────────────

let agoraExtension: any = null;

/**
 * Pipes RNNoise into an Agora microphone track:
 * `track → RNNoise → track.processorDestination`. Agora keeps owning the track —
 * device switch, mute, volume and level all work as before. Does not wait for
 * the worklet: the graph connects when Agora hands the processor its input, and
 * `denoiserStateOf(track)` reads the outcome. Never rejects.
 */
export async function attachAgoraDenoiser(AgoraRTC: any, track: any): Promise<void> {
    if (Platform.OS !== 'web' || !track?.pipe || !track.processorDestination) return;
    try {
        const { AudioExtension, AudioProcessor } = await import('agora-rte-extension');
        if (!agoraExtension) {
            class RnnoiseProcessor extends AudioProcessor {
                public name = 'RnnoiseProcessor';
                public denoiser: DenoiserState | null = null;
                private node: AudioWorkletNode | null = null;
                protected async onNode(audioNode: AudioNode, context: any) {
                    destroyRnnoiseNode(this.node);
                    this.node = null;
                    const ctx = context.getAudioContext?.();
                    if (!canDenoise(ctx)) {
                        this.denoiser = 'unsupported';
                        this.output(audioNode, context);
                        return;
                    }
                    try {
                        const node = await createRnnoiseNode(ctx);
                        audioNode.connect(node);
                        this.node = node;
                        this.denoiser = 'rnnoise';
                        this.output(node, context);
                    } catch {
                        this.denoiser = 'failed';
                        this.output(audioNode, context);
                    }
                }
                protected onUnpiped() {
                    destroyRnnoiseNode(this.node);
                    this.node = null;
                }
            }
            class RnnoiseExtension extends AudioExtension<RnnoiseProcessor> {
                public name = 'RnnoiseExtension';
                protected _createProcessor() { return new RnnoiseProcessor(); }
            }
            agoraExtension = new RnnoiseExtension();
            AgoraRTC.registerExtensions([agoraExtension]);
        }
        const processor = agoraExtension.createProcessor();
        track.pipe(processor).pipe(track.processorDestination);
        attached.set(track, () => processor.denoiser);
    } catch {
        attached.set(track, () => 'failed');
    }
}

// ─── LiveKit: a TrackProcessor on the local mic ─────────────────────────────

/**
 * `LocalAudioTrack.setProcessor` contract: build `processedTrack` from the raw
 * track in `init`, rebuild in `restart` (device switch), tear down in `destroy`.
 */
export function createLiveKitDenoiser() {
    let ctx: AudioContext | null = null;
    let source: MediaStreamAudioSourceNode | null = null;
    let node: AudioWorkletNode | null = null;
    let dest: MediaStreamAudioDestinationNode | null = null;
    const teardown = () => {
        destroyRnnoiseNode(node);
        try { source?.disconnect(); } catch { /* detached */ }
        node = null; source = null; dest = null;
    };
    const processor = {
        name: 'rnnoise',
        processedTrack: undefined as MediaStreamTrack | undefined,
        denoiser: null as DenoiserState | null,
        async init({ track }: { track: MediaStreamTrack }) {
            teardown();
            // LiveKit's own context runs at the device rate; RNNoise needs 48 kHz.
            try {
                if (!ctx || ctx.state === 'closed') ctx = new AudioContext({ sampleRate: RNNOISE_SAMPLE_RATE });
            } catch {
                ctx = null;
            }
            if (!canDenoise(ctx)) {
                processor.denoiser = 'unsupported';
                processor.processedTrack = track;
                return;
            }
            try {
                node = await createRnnoiseNode(ctx);
                source = ctx.createMediaStreamSource(new MediaStream([track]));
                dest = ctx.createMediaStreamDestination();
                source.connect(node).connect(dest);
                if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
                processor.processedTrack = dest.stream.getAudioTracks()[0];
                processor.denoiser = 'rnnoise';
            } catch {
                teardown();
                processor.denoiser = 'failed';
                processor.processedTrack = track;
            }
        },
        /** Ties the processor's state to the track for `denoiserStateOf`. */
        bind(track: object) { attached.set(track, () => processor.denoiser); },
        async restart(opts: { track: MediaStreamTrack }) { await processor.init(opts); },
        async destroy() {
            teardown();
            processor.processedTrack = undefined;
            const c = ctx;
            ctx = null;
            if (c && c.state !== 'closed') await c.close().catch(() => {});
        },
    };
    return processor;
}

/**
 * The room's Agora mic: created as before, then piped through RNNoise when
 * enabled — except a headset mic, which stays off the WebAudio graph (CORE-1669).
 */
export async function createAgoraMicTrack(AgoraRTC: any, config: any, denoise: boolean): Promise<any> {
    const track = await AgoraRTC.createMicrophoneAudioTrack(config);
    if (!denoise) return track;
    if (micFitsWebAudioGraph(track)) await attachAgoraDenoiser(AgoraRTC, track);
    // CORE-1598 — said apart from «flag off»: dev room 6227 (20.10) read `off` after
    // a switch to a headset, which looks like the denoiser was never enabled.
    else attached.set(track, () => 'headset');
    return track;
}
